import { STR_NO, STR_YES } from "../constants";
import {
    IBasket,
    IBasketLine,
    IOptionValue,
    IProduct,
} from "../models/catalogue";
import { APIPrice, IAPIPrice, IPrice } from "../models/prices";
import { getCurrentLocale } from "./ajax";
import { Dinero, ZERO, getDinero, sum } from "./money";
import { assertNever } from "./never";

export const isMattress = (product: IProduct): boolean => {
    return product.product_class_slug === "mattress";
};

export const getOptionValueHash = (value: IOptionValue): string => {
    return value.toString();
};

export const asMultiValueOption = <T>(
    value: T | readonly T[] | T[],
): string[] => {
    if (Array.isArray(value)) {
        return value.map((v) => `${v}`);
    }
    return [`${value}`];
};

/**
 * Get the user-facing label for a product attribute.
 *
 * Boolean options (value "1"/"0") are handled specially: "1" returns the
 * attribute name (e.g. "Power Base"), "0" returns "" so the caller can
 * filter it out.
 *
 * For option-type attributes the API returns a `display_names` dict keyed
 * by language code (e.g. `{"en": "Soft", "fr-ca": "Mi-Ferme"}`).  This
 * function resolves the current page locale via `getCurrentLocale()` and
 * returns the matching translation, falling back to the first available
 * translation, then to the raw option value string.
 */
export const getOptionValueDisplayName = (attr: {
    readonly name?: string;
    readonly value: IOptionValue;
    readonly display_names?: Readonly<Record<string, string>> | null;
}): string => {
    // Boolean attributes: "1" → attribute name, "0" → empty string
    if (attr.value === STR_YES) {
        return attr.name ?? "";
    }
    if (attr.value === STR_NO) {
        return "";
    }
    if (attr.display_names) {
        // Normalize to lowercase to avoid mismatches between
        // document.documentElement.lang (e.g. "fr-CA") and backend
        // locale codes (e.g. "fr-ca").
        const locale = getCurrentLocale().toLowerCase();
        const translated = attr.display_names[locale];
        if (translated) {
            return translated;
        }
        // Fallback to first available translation
        const values = Object.values(attr.display_names);
        if (values.length > 0) {
            return values[0];
        }
    }
    return asMultiValueOption(attr.value).sort().join(" & ");
};

export const getPostDiscountAddonsTotal = (
    price: IPrice,
    qty: number,
): Dinero => {
    const total = (price.post_discount_addons || [])
        .map((addon) => getDinero(addon.price_excl_tax))
        .reduce((memo, addonPrice) => memo.add(addonPrice), ZERO)
        .multiply(qty);
    return total;
};

export const enum PriceType {
    RETAIL, // MSRP (retail price)
    ACTUAL_EXCL_TAX, // Base price in Oscar
    ACTUAL_INCL_TAX,
    COSMETIC_EXCL_TAX, // Predicted base price after applying offers
    COSMETIC_INCL_TAX,
}

export const getProductPrice = (
    inputPrice: IPrice | IAPIPrice,
    {
        priceType,
        includePostDiscountAddons,
        quantity,
    }: {
        priceType: PriceType;
        includePostDiscountAddons: boolean;
        quantity: number;
    },
): Dinero => {
    // Figure out what kind of price we have to work with.
    // - IPrice is always a unit price
    // - IAPIPrice has both unit and total, but the total might not be the
    //   quantity requested.
    // let unitPrice: IPrice;
    let basePrice: IPrice;
    let basePriceIncludesQuantity: boolean;
    if (APIPrice.is(inputPrice)) {
        if (inputPrice.quantity === quantity) {
            // unitPrice = inputPrice.unit;
            basePrice = inputPrice.total;
            basePriceIncludesQuantity = true;
        } else {
            // unitPrice = inputPrice.unit;
            basePrice = inputPrice.unit;
            basePriceIncludesQuantity = false;
        }
    } else {
        // unitPrice = inputPrice;
        basePrice = inputPrice;
        basePriceIncludesQuantity = false;
    }

    // Get the base price based on the price type options
    let outputPrice: Dinero;
    switch (priceType) {
        case PriceType.RETAIL: {
            outputPrice = getDinero(
                basePrice.retail ??
                    basePrice.cosmetic_excl_tax ??
                    basePrice.excl_tax,
            );
            break;
        }

        case PriceType.ACTUAL_EXCL_TAX: {
            outputPrice = getDinero(basePrice.excl_tax);
            break;
        }

        case PriceType.ACTUAL_INCL_TAX: {
            outputPrice = getDinero(basePrice.incl_tax);
            break;
        }

        case PriceType.COSMETIC_EXCL_TAX: {
            outputPrice = getDinero(
                basePrice.cosmetic_excl_tax ?? basePrice.excl_tax,
            );
            break;
        }

        case PriceType.COSMETIC_INCL_TAX: {
            outputPrice = getDinero(
                basePrice.cosmetic_incl_tax ?? basePrice.incl_tax,
            );
            break;
        }
    }

    // If quantity isn't already accounted for, do that now.
    if (!basePriceIncludesQuantity) {
        outputPrice = outputPrice.multiply(quantity);
    }

    // Add on the post-discount addons
    if (includePostDiscountAddons) {
        const addonsTotal = getPostDiscountAddonsTotal(basePrice, quantity);
        outputPrice = outputPrice.add(addonsTotal);
    }

    return outputPrice;
};

export const isProductPriceDiscounted = (price: IPrice): boolean => {
    const retail = getProductPrice(price, {
        priceType: PriceType.RETAIL,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    const cosmetic = getProductPrice(price, {
        priceType: PriceType.COSMETIC_EXCL_TAX,
        includePostDiscountAddons: true,
        quantity: 1,
    });
    return cosmetic.lessThan(retail);
};

export const enum LinePriceType {
    RETAIL,
    EXCL_TAX_EXCL_DISCOUNTS,
    INCL_TAX_EXCL_DISCOUNTS,
    EXCL_TAX_INCL_DISCOUNTS,
    INCL_TAX_INCL_DISCOUNTS,
}

export const getBasketLinePrice = (
    basket: IBasket,
    line: IBasketLine,
    {
        priceType,
        includePostDiscountAddons = true,
    }: {
        priceType: LinePriceType;
        includePostDiscountAddons?: boolean;
    },
): Dinero => {
    // Find all of the lines which are children of this line
    const childLines = basket.lines.filter((_line) => _line.parent === line.id);
    // If any child lines exist, then this line's price is the sum of those
    // children. Else, just use the single standalone line.
    const lines = childLines.length > 0 ? childLines : [line];
    // Get prices from the lines based on the price type options
    let outputPrice: Dinero;
    switch (priceType) {
        case LinePriceType.RETAIL: {
            // Retail [prices are a special case—their actually based on the product.
            outputPrice = getProductPrice(line.product.price, {
                priceType: PriceType.RETAIL,
                includePostDiscountAddons: false,
                quantity: line.quantity,
            });
            break;
        }
        case LinePriceType.EXCL_TAX_INCL_DISCOUNTS: {
            outputPrice = sum(
                lines.map((line) => getDinero(line.price_excl_tax)),
            );
            break;
        }
        case LinePriceType.INCL_TAX_INCL_DISCOUNTS: {
            outputPrice = sum(
                lines.map((line) => getDinero(line.price_incl_tax)),
            );
            break;
        }
        case LinePriceType.EXCL_TAX_EXCL_DISCOUNTS: {
            outputPrice = sum(
                lines.map((line) =>
                    getDinero(line.price_excl_tax_excl_discounts),
                ),
            );
            break;
        }
        case LinePriceType.INCL_TAX_EXCL_DISCOUNTS: {
            outputPrice = sum(
                lines.map((line) =>
                    getDinero(line.price_incl_tax_excl_discounts),
                ),
            );
            break;
        }
        default: {
            /* eslint-disable-next-line @typescript-eslint/only-throw-error */
            throw assertNever(priceType);
        }
    }

    // If instructed, total the post-discount addons for the line and subtract
    // it from the output price.
    if (!includePostDiscountAddons) {
        const addonsTotal = getPostDiscountAddonsTotal(
            line.product.price,
            line.quantity,
        );
        outputPrice = outputPrice.subtract(addonsTotal);
    }

    // Return the sum
    return outputPrice;
};

/**
 * Get the currently active promo callout from a product's promo_callout field.
 * Returns the callout text that is currently within its date range, or null if none are active.
 * If multiple callouts are active, returns the one ending soonest.
 *
 * This function handles both the old format (string | null) and new format (array of callouts)
 * for backward compatibility during the cache transition period.
 */
export const getActivePromoCallout = (
    promoCallouts: IProduct["promo_callout"] | string | null,
): string | null => {
    // TEMPORARY: Handle old cached format (string | null)
    // This check can be removed after all cached API responses have expired (~ 24-48 hours after deployment)
    if (typeof promoCallouts === "string") {
        return promoCallouts;
    }

    // New format: array of promo callouts
    if (
        !promoCallouts ||
        !Array.isArray(promoCallouts) ||
        promoCallouts.length === 0
    ) {
        return null;
    }

    const now = new Date();

    // Find all currently active promos
    const activePromos = promoCallouts.filter((promo) => {
        if (!promo.start_date || !promo.end_date) {
            return false;
        }
        const startDate = new Date(promo.start_date);
        const endDate = new Date(promo.end_date);
        return startDate <= now && now <= endDate;
    });

    if (activePromos.length === 0) {
        return null;
    }

    // If multiple active promos, return the one ending soonest
    // (matching the backend's original ordering logic)
    activePromos.sort((a, b) => {
        const endA = new Date(a.end_date);
        const endB = new Date(b.end_date);
        return endA.getTime() - endB.getTime();
    });

    return activePromos[0].text;
};
